import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
} from "@mui/material";
import { adminRegister, getInviteInfo } from "../../api/auth";
import { ROLES } from "../../constants/roles";
import { getStatesByCountry, getAllCountries } from "../../lib/regions";

const NIGERIAN_STATES = getStatesByCountry("Nigeria");
const OTHER_COUNTRIES = getAllCountries()
  .map((c) => c.country)
  .filter((country) => country !== "Nigeria");

export default function AdminRegister() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    name: "",
    password: "",
    phone: "",
  });
  const [invitedRole, setInvitedRole] = useState(null);
  const [market, setMarket] = useState("nigeria");
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [lagosZones, setLagosZones] = useState([]);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const needsTerritory = invitedRole === ROLES.SALES_ACCOUNT_SPECIALIST;
  const needsLagosZone = needsTerritory && market === "nigeria" && states.includes("Lagos");

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing invite token");
      return;
    }
    (async () => {
      try {
        const res = await getInviteInfo(token);
        setInvitedRole(res.data.role);
      } catch (err) {
        setError(err.response?.data?.message || "Invalid or expired invite token");
      }
    })();
  }, [token]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (needsTerritory) {
      if (market === "nigeria" && states.length === 0) {
        setError("Select at least one state you cover");
        return;
      }
      if (market === "other" && countries.length === 0) {
        setError("Select at least one country you cover");
        return;
      }
      if (needsLagosZone && lagosZones.length === 0) {
        setError("Select whether you cover Lagos Island or Mainland");
        return;
      }
    }

    try {
      await adminRegister({
        ...formData,
        token,
        ...(needsTerritory ? { market, states, countries } : {}),
        ...(needsLagosZone ? { lagosZones } : {}),
      });
      setSuccess("Registration successful! Redirecting to login...");
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed");
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 440,
        mx: "auto",
        mt: 8,
        p: 3,
        boxShadow: 3,
        borderRadius: 2,
      }}
    >
      <Typography variant="h5" mb={2}>
        Admin Registration
      </Typography>

      {success && <Alert severity="success">{success}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <TextField
          label="Full Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
        <TextField
          label="Phone Number"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
        />
        <TextField
          label="Password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          required
        />

        {needsTerritory && (
          <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" mb={1}>
              Which market do you cover?
            </Typography>
            <ToggleButtonGroup
              value={market}
              exclusive
              onChange={(e, val) => val && setMarket(val)}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="nigeria">Nigeria</ToggleButton>
              <ToggleButton value="other">Other country</ToggleButton>
            </ToggleButtonGroup>

            {market === "nigeria" ? (
              <>
                <Autocomplete
                  multiple
                  options={NIGERIAN_STATES}
                  value={states}
                  onChange={(e, val) => setStates(val)}
                  renderInput={(params) => (
                    <TextField {...params} label="States you cover" placeholder="Add state" />
                  )}
                />
                {needsLagosZone && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" mb={1}>
                      Which part of Lagos do you cover?
                    </Typography>
                    <ToggleButtonGroup
                      value={lagosZones}
                      onChange={(e, val) => setLagosZones(val)}
                      size="small"
                      fullWidth
                    >
                      <ToggleButton value="island">Island</ToggleButton>
                      <ToggleButton value="mainland">Mainland</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
              </>
            ) : (
              <Autocomplete
                multiple
                options={OTHER_COUNTRIES}
                value={countries}
                onChange={(e, val) => setCountries(val)}
                renderInput={(params) => (
                  <TextField {...params} label="Countries you cover" placeholder="Add country" />
                )}
              />
            )}
          </Box>
        )}

        <Button variant="contained" type="submit" disabled={!token}>
          Complete Registration
        </Button>
      </Box>
    </Box>
  );
}
